/**
 * QMS AI Investigation Section
 * Displays AI-powered investigation findings for a ticket
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Lightbulb,
  AlertTriangle,
  Shield,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useQmsAiInvestigation,
  useRetryAiInvestigation,
  useTriggerAiInvestigation,
  useAcceptAiFindings,
} from '../../../hooks/useQmsAiInvestigation';
import type {
  QmsAiInvestigation,
  QmsAiRootCause,
  QmsAiRiskAssessment,
  QmsAiConfidence,
  QmsRiskLevel,
} from '../../../types/qms.types';
import { ApplyAiFindingsDialog } from './ApplyAiFindingsDialog';
import { QmsCodeFixStatus } from './QmsCodeFixStatus';

interface QmsAiInvestigationSectionProps {
  ticketId: string;
  defaultExpanded?: boolean;
  /** If true, shows minimal indicator instead of full section (findings already applied to ticket) */
  hasAppliedFindings?: boolean;
}

/**
 * Confidence badge colors
 */
function getConfidenceBadgeClass(confidence: QmsAiConfidence): string {
  switch (confidence) {
    case 'high':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Risk level badge colors and icons
 */
function getRiskLevelConfig(level: QmsRiskLevel): {
  className: string;
  icon: React.ReactNode;
} {
  switch (level) {
    case 'critical':
      return {
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: <AlertCircle className="h-3 w-3" />,
      };
    case 'high':
      return {
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <AlertTriangle className="h-3 w-3" />,
      };
    case 'medium':
      return {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: <Shield className="h-3 w-3" />,
      };
    case 'low':
      return {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle2 className="h-3 w-3" />,
      };
    default:
      return {
        className: 'bg-gray-100 text-gray-800',
        icon: <Shield className="h-3 w-3" />,
      };
  }
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: QmsAiInvestigation['status'] }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    case 'in_progress':
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Analyzing
        </Badge>
      );
    case 'completed':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Complete
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    default:
      return null;
  }
}

/**
 * Root cause card component
 */
function RootCauseCard({ cause, index }: { cause: QmsAiRootCause; index: number }) {
  return (
    <div className="p-3 bg-white rounded-lg border border-gray-200 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
            {index + 1}
          </span>
          <span className="font-medium text-gray-900">{cause.cause}</span>
        </div>
        <Badge
          variant="outline"
          className={cn('text-xs capitalize', getConfidenceBadgeClass(cause.confidence))}
        >
          {cause.confidence}
        </Badge>
      </div>
      <p className="text-sm text-gray-600 ml-7">{cause.explanation}</p>
      {cause.evidence && cause.evidence.length > 0 && (
        <div className="ml-7">
          <p className="text-xs font-medium text-gray-500 mb-1">Evidence:</p>
          <ul className="text-xs text-gray-500 list-disc list-inside">
            {cause.evidence.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Risk assessment card component
 */
function RiskAssessmentCard({ assessment }: { assessment: QmsAiRiskAssessment }) {
  const config = getRiskLevelConfig(assessment.level);

  return (
    <div className="p-3 bg-white rounded-lg border border-gray-200 space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={cn('capitalize', config.className)}>
          {config.icon}
          <span className="ml-1">{assessment.level} Risk</span>
        </Badge>
      </div>
      <p className="text-sm text-gray-600">{assessment.reasoning}</p>
      {assessment.factors && assessment.factors.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Risk Factors:</p>
          <ul className="text-xs text-gray-500 list-disc list-inside">
            {assessment.factors.map((factor, i) => (
              <li key={i}>{factor}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function QmsAiInvestigationSection({
  ticketId,
  defaultExpanded = true,
  hasAppliedFindings = false,
}: QmsAiInvestigationSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [findingsApplied, setFindingsApplied] = useState(hasAppliedFindings);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [viewLatest, setViewLatest] = useState(false);

  const { data: investigation, isLoading, error } = useQmsAiInvestigation(ticketId);
  const retryMutation = useRetryAiInvestigation();
  const triggerMutation = useTriggerAiInvestigation();
  const rerunMutation = useTriggerAiInvestigation(true);
  const acceptMutation = useAcceptAiFindings();
  const running = investigation?.status === 'pending' || investigation?.status === 'in_progress'
    || ['pending', 'in_progress'].includes(investigation?.findings?.codeFix?.status ?? '');
  const canRerun = investigation?.status === 'completed' && !running;

  function handleRerun() {
    rerunMutation.mutate(ticketId, {
      onSuccess: () => {
        setFindingsApplied(false);
        setViewLatest(true);
        setExpanded(true);
        acceptMutation.reset();
        toast.success('A new AI investigation has been queued');
      },
      onError: (err) => toast.error(`Failed to restart investigation: ${(err as Error).message}`),
    });
  }

  const rerunButton = canRerun ? (
    <Button type="button" variant="outline" size="sm" onClick={handleRerun} disabled={rerunMutation.isPending}>
      <RefreshCw className={cn('h-4 w-4 mr-2', rerunMutation.isPending && 'animate-spin')} />
      {rerunMutation.isPending ? 'Queuing…' : 'Run again'}
    </Button>
  ) : null;

  // If findings have already been applied (from props or after apply action), show minimal indicator
  if ((hasAppliedFindings || findingsApplied) && !viewLatest && !running) {
    return (
      <div className="border-t pt-6">
        <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 rounded-lg px-4 py-3 border border-purple-200">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <span className="font-medium">AI Investigation Applied</span>
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-purple-600 ml-auto text-xs">
            View details in Activity Timeline
          </span>
        </div>
        <div className="mt-3 flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setViewLatest(true)}>View investigation</Button>
          {rerunButton}
        </div>
        <QmsCodeFixStatus fix={investigation?.findings?.codeFix} onRetry={handleRetry} isRetrying={retryMutation.isPending} />
      </div>
    );
  }

  function handleRetry() {
    retryMutation.mutate(ticketId, {
      onSuccess: () => {
        toast.success('AI investigation restarted');
      },
      onError: (err) => {
        toast.error(`Failed to restart investigation: ${(err as Error).message}`);
      },
    });
  }

  const handleTrigger = () => {
    triggerMutation.mutate(ticketId, {
      onSuccess: () => {
        toast.success('AI investigation started');
      },
      onError: (err) => {
        toast.error(`Failed to start investigation: ${(err as Error).message}`);
      },
    });
  };

  const handleApplyClick = () => {
    setApplyDialogOpen(true);
  };

  const handleConfirmApply = () => {
    acceptMutation.mutate(
      { ticketId, fields: ['all'] },
      {
        onSuccess: () => {
          setFindingsApplied(true);
          setApplyDialogOpen(false);
          toast.success('AI findings applied to ticket', {
            description: 'Summary and Risk Assessment appended to Investigation. Other fields updated.',
          });
        },
        onError: (err) => {
          toast.error(`Failed to apply findings: ${(err as Error).message}`);
        },
      }
    );
  };

  // Show section even if no investigation exists (to allow manual trigger)
  const noInvestigation = !investigation && !isLoading;

  return (
    <div className="border-t pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-lg font-semibold text-purple-900 hover:text-purple-700"
      >
        {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        <Sparkles className="h-5 w-5 text-purple-600" />
        AI Investigation
        {investigation && <StatusBadge status={investigation.status} />}
      </button>
      {rerunButton}
      </div>
      <QmsCodeFixStatus fix={investigation?.findings?.codeFix} onRetry={handleRetry} isRetrying={retryMutation.isPending} />

      {expanded && (
        <div className="mt-4 space-y-4 bg-purple-50 rounded-lg p-4 border border-purple-100">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center gap-2 text-purple-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading AI investigation...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="text-red-600 text-sm">
              Failed to load AI investigation: {(error as Error).message}
            </div>
          )}

          {/* No investigation exists - show trigger button */}
          {noInvestigation && !error && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-900">No AI Investigation Yet</p>
                  <p className="text-sm text-purple-700">
                    Start an AI-powered investigation to analyze this ticket, identify potential root causes, and get recommended actions.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleTrigger}
                disabled={triggerMutation.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {triggerMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Start AI Investigation
              </Button>
              {triggerMutation.isError && (
                <p className="text-sm text-red-600">
                  Failed to start investigation: {(triggerMutation.error as Error).message}
                </p>
              )}
            </div>
          )}

          {/* Pending/In Progress state */}
          {investigation && (investigation.status === 'pending' || investigation.status === 'in_progress') && (
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <div>
                <p className="font-medium text-purple-900">AI is analyzing this ticket...</p>
                <p className="text-sm text-purple-600">
                  This typically takes 30-60 seconds. The page will update automatically.
                </p>
              </div>
            </div>
          )}

          {/* Failed state */}
          {investigation?.status === 'failed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900">Investigation Failed</p>
                  <p className="text-sm text-red-700">
                    {investigation.errorMessage || 'An unexpected error occurred during the investigation.'}
                  </p>
                  {investigation.retryCount > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Retry attempts: {investigation.retryCount}
                    </p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRetry}
                disabled={retryMutation.isPending}
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                {retryMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Retry Investigation
              </Button>
            </div>
          )}

          {/* Completed state with findings */}
          {investigation?.status === 'completed' && investigation.findings && (
            <div className="space-y-6">
              {/* Summary */}
              <div>
                <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Summary
                </h4>
                <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-200">
                  {investigation.findings.summary}
                </p>
              </div>

              {/* Potential Root Causes */}
              {investigation.findings.potentialRootCauses.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">
                    Potential Root Causes
                  </h4>
                  <div className="space-y-2">
                    {investigation.findings.potentialRootCauses.map((cause, index) => (
                      <RootCauseCard key={index} cause={cause} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Actions */}
              {investigation.findings.recommendedActions.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">
                    Recommended Actions
                  </h4>
                  <ul className="space-y-1 bg-white p-3 rounded-lg border border-gray-200">
                    {investigation.findings.recommendedActions.map((action, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-purple-600 font-medium">{index + 1}.</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risk Assessment */}
              {investigation.findings.riskAssessment && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">
                    Risk Assessment
                  </h4>
                  <RiskAssessmentCard assessment={investigation.findings.riskAssessment} />
                </div>
              )}

              {/* Suggested Tags */}
              {investigation.findings.suggestedTags && investigation.findings.suggestedTags.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Suggested Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {investigation.findings.suggestedTags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="bg-purple-100 text-purple-800">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Issues */}
              {investigation.findings.relatedIssues.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">
                    Related Issues
                  </h4>
                  <ul className="space-y-1 bg-white p-3 rounded-lg border border-gray-200">
                    {investigation.findings.relatedIssues.map((issue, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="font-mono text-purple-600">{issue.ticketId}</span>
                        <span className="text-gray-500">-</span>
                        <span className="text-gray-700">{issue.title}</span>
                        <span className="text-xs text-gray-500">({issue.relationship})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Token usage info */}
              <div className="text-xs text-gray-500 flex items-center gap-4 pt-2 border-t border-purple-100">
                <span>Model: {investigation.modelId}</span>
                {investigation.inputTokens && (
                  <span>Input tokens: {investigation.inputTokens.toLocaleString()}</span>
                )}
                {investigation.outputTokens && (
                  <span>Output tokens: {investigation.outputTokens.toLocaleString()}</span>
                )}
              </div>

              {/* Accept Findings Button */}
              <div className="flex justify-end items-center gap-3 pt-2">
                {findingsApplied || acceptMutation.isSuccess ? (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-1.5 rounded-md border border-green-200">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm font-medium">Findings applied to ticket</span>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={handleApplyClick}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {acceptMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    Apply AI Findings to Ticket
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Apply Findings Confirmation Dialog */}
      {investigation?.status === 'completed' && investigation.findings && (
        <ApplyAiFindingsDialog
          open={applyDialogOpen}
          onOpenChange={setApplyDialogOpen}
          investigation={investigation}
          onConfirm={handleConfirmApply}
          isPending={acceptMutation.isPending}
        />
      )}
    </div>
  );
}
